import React from 'react';
import Icon from '../../../components/AppIcon';

const WhyDifferentSection = () => {
  const differentiators = [
    {
      icon: 'Brain',
      title: 'Focus on Neurology',
      subtitle: 'Specialized expertise where it matters most',
      description: 'Unlike generic health apps, MycoMind is specifically designed for neurological disorders, with features and AI models trained on neurological data patterns.',
      highlights: [
        'Neurological symptom taxonomy',
        'Cognitive assessment protocols',
        'Specialized pattern recognition',
        'Disorder-specific insights'
      ],
      color: 'primary'
    },
    {
      icon: 'Sparkles',
      title: 'Beyond Basic Tracking',
      subtitle: 'Intelligence that transforms data into wisdom',
      description: 'We go far beyond simple data collection. Our AI generates actionable insights, identifies correlations, and provides predictive analytics that help both patients and providers.',
      highlights: [
        'Predictive symptom modeling',
        'Multi-dimensional correlation analysis',
        'Personalized trend identification',
        'Evidence-based recommendations'
      ],
      color: 'warning'
    },
    {
      icon: 'Users',
      title: 'Bridging the Gap',
      subtitle: 'Revolutionizing patient-provider communication',
      description: 'Our central mission is improving communication between patients and providers. Every feature is designed to enhance understanding and collaborative care.',
      highlights: [
        'Real-time data sharing',
        'Unified health narratives',
        'Collaborative care planning',
        'Improved appointment efficiency'
      ],
      color: 'accent'
    }
  ];

  const ComparisonPoint = ({ highlight, color }) => (
    <div className="flex items-center space-x-3 py-2">
      <div className={`w-5 h-5 bg-${color}/10 rounded-full flex items-center justify-center flex-shrink-0`}>
        <Icon name="Check" size={12} className={`text-${color}`} />
      </div>
      <span className="text-muted-foreground">{highlight}</span>
    </div>
  );

  return (
    <section id="why-different" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Why We're Different
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              MycoMind stands apart from generic health applications through our specialized approach, 
              advanced AI capabilities, and unwavering focus on improving patient-provider relationships.
            </p>
          </div>

          {/* Differentiators Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {differentiators?.map((diff, index) => (
              <div key={index} className="group">
                <div className="bg-card border border-border rounded-2xl p-8 h-full hover:shadow-lg transition-all duration-300 hover:border-primary/20">
                  {/* Icon & Header */}
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 bg-${diff?.color}/10 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon name={diff?.icon} size={32} className={`text-${diff?.color}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-card-foreground mb-2 group-hover:text-primary transition-colors">
                      {diff?.title}
                    </h3>
                    <p className={`text-sm font-medium text-${diff?.color} mb-4`}>
                      {diff?.subtitle}
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {diff?.description}
                  </p>

                  {/* Highlights */}
                  <div className="space-y-2">
                    {diff?.highlights?.map((highlight, idx) => (
                      <ComparisonPoint key={idx} highlight={highlight} color={diff?.color} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Value Proposition Summary */}
          <div className="bg-gradient-to-r from-primary/5 via-warning/5 to-accent/5 rounded-2xl p-8 md:p-12 text-center">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                The MycoMind Advantage
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                While other health apps simply collect data, MycoMind transforms that data into meaningful insights 
                that drive better health outcomes. Our neurological expertise, advanced AI, and focus on 
                communication make us the premier choice for neurological health management.
              </p>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">95%</div>
                  <div className="text-sm text-muted-foreground">Improved Communication</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-warning mb-2">87%</div>
                  <div className="text-sm text-muted-foreground">Better Insights</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent mb-2">92%</div>
                  <div className="text-sm text-muted-foreground">Provider Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">89%</div>
                  <div className="text-sm text-muted-foreground">Patient Engagement</div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border/50">
                <p className="text-sm text-muted-foreground">
                  * Based on pilot studies with healthcare institutions and patient feedback surveys
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyDifferentSection;